import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Typography, CircularProgress, Alert } from "@mui/material";
import {
  createTemplate,
  setShouldCreate,
} from "../../../../redux/Slice/Templates/templateCreationSlice";
import TemplateCreationPreview from "../Preview/TemplateCreationPreview";

const AuthenticationSubmission = () => {
  const dispatch = useDispatch();

  // Pull relevant data from templateCreation slice
  const {
    templateId,
    language,
    allowAutoCategoryChange,
    components, // This will contain the authentication components
    shouldCreate,
    status,
    error,
  } = useSelector((state) => state.templateCreation);

  /**
   * Build authentication template payload with the dynamic structure from form
   */
  const buildAuthenticationPayload = () => {
    const templateData = {
      name: templateId || "authentication_template",
      language: language || "en_US",
      category: "AUTHENTICATION",
      allowAutoCategoryChange: !!allowAutoCategoryChange,
      components: components || [
        {
          type: "BODY",
          add_security_recommendation: true
        },
        {
          type: "FOOTER",
          code_expiration_minutes: 10
        },
        {
          type: "BUTTONS",
          buttons: [
            {
              type: "OTP",
              otp_type: "COPY_CODE",
              text: "Copy Code"
            }
          ]
        }
      ]
    };

    return templateData;
  };

  /**
   * Handle the actual dispatch to create the authentication template.
   */
  const createAuthenticationTemplate = () => {
    const finalPayload = buildAuthenticationPayload();

    dispatch(createTemplate(JSON.stringify(finalPayload, null, 2)))
      .unwrap()
      .then(() => {
        // Prevent repeated submission if the user remains on the page
        dispatch(setShouldCreate(false));
      })
      .catch((err) => {
        // Prevent repeated submission if the user remains on the page
        dispatch(setShouldCreate(false));
      });
  };

  /**
   * Watch for `shouldCreate` becoming `true`.
   * Then run the create, and set it back to false.
   */
  useEffect(() => {
    if (shouldCreate) {
      // Immediately set false so it won't re-trigger
      dispatch(setShouldCreate(false));
      createAuthenticationTemplate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldCreate]);

  return (
    <Box sx={{ padding: 2 }}>
      <Typography
        variant="h6"
        gutterBottom
        sx={{ color: (theme) => theme.palette.text.primary }}
      >
        Authentication Template Submission
      </Typography>

      {/* Loading Spinner */}
      {status === "loading" && (
        <Box sx={{ my: 2 }}>
          <CircularProgress
            sx={{ color: (theme) => theme.palette.primary.main }}
          />
        </Box>
      )}

      {/* Error Alert */}
      {status === "failed" && error && (
        <Alert severity="error" sx={{ my: 2 }}>
          <strong>Error:</strong>{" "}
          {typeof error === "string"
            ? error
            : error?.details?.error?.error_user_msg ||
              "Failed to create authentication template. Please try again."}
        </Alert>
      )}

      {/* Success Alert */}
      {status === "succeeded" && (
        <Alert severity="success" sx={{ my: 2 }}>
          Authentication template created successfully!
        </Alert>
      )}

      {/* Name & Language */}
      <Typography
        variant="body1"
        sx={{ color: (theme) => theme.palette.text.primary }}
      >
        <strong>Name (templateId):</strong> {templateId || "N/A"}
      </Typography>
      <Typography
        variant="body1"
        sx={{ color: (theme) => theme.palette.text.primary }}
      >
        <strong>Language:</strong> {language || "en_US"}
      </Typography>

      {/* Preview — shown here inline for this last step instead of the
          right-side panel (TemplatePreview.jsx renders nothing on this
          step so it isn't duplicated there). */}
      <Box sx={{ mt: 3, display: "flex", flexDirection: "column", alignItems: "center" }}>
        <Typography
          variant="h6"
          sx={{ mb: 2, color: (theme) => theme.palette.text.primary }}
        >
          Preview
        </Typography>
        <TemplateCreationPreview />
      </Box>
    </Box>
  );
};

export default AuthenticationSubmission; 